'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useTranslation } from 'react-i18next';

// UI lib dependencies
import {
  ActionIcon,
  Button,
  CopyButton as NativeCopyButton,
  Tooltip,
} from '@mantine/core';

// Assets
import {
  IconCopy as IconCopyDefault,
  IconCheck as IconCopiedDefault,
} from '@tabler/icons-react';

// Types
import { CopyButtonProps } from './types';

// Style
import { ICON_SIZE_MAP } from '@ui-kit/icons-sizes';
import { palette } from '@ui-kit/colors';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function CopyButton({
  withLabel = false,
  tooltipProps,
  iconCopy,
  iconCopied,
  ...props
}: CopyButtonProps) {
  /* ********************************** HOOKS ********************************* */

  const { t } = useTranslation('Common');

  /* ***************************** LOCAL VARIABLES **************************** */

  const IconCopy = iconCopy ?? IconCopyDefault;
  const IconCopied = iconCopied ?? IconCopiedDefault;

  /* ******************************** RENDERING ******************************* */
  return (
    <NativeCopyButton timeout={2000} {...props}>
      {({ copied, copy }) => (
        <Tooltip
          label={copied ? t('cta.copied') : t('cta.copy')}
          position="right"
          withArrow
          withinPortal
          {...tooltipProps}
        >
          {withLabel ? (
            <Button
              variant="subtle"
              size="xs"
              bg={palette.blackroomLightGray1}
              c="blackroomBlack"
              {...props.buttonProps}
              leftSection={
                copied ? (
                  <IconCopied size={ICON_SIZE_MAP.small} strokeWidth={1} />
                ) : (
                  <IconCopy size={ICON_SIZE_MAP.small} strokeWidth={1} />
                )
              }
              onClick={copy}
            >
              {copied ? t('cta.copied') : t('cta.copy')}
            </Button>
          ) : (
            <ActionIcon
              variant="transparent"
              color={copied ? 'teal' : 'blackroomBlue'}
              onClick={copy}
            >
              {copied ? (
                <IconCopied size="1rem" />
              ) : (
                <IconCopy size={ICON_SIZE_MAP.small} />
              )}
            </ActionIcon>
          )}
        </Tooltip>
      )}
    </NativeCopyButton>
  );
}

export default CopyButton;
